import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { 
  BookOpen, MessageCircle, PenTool, FileText, Trophy, 
  LogOut, Star, Target, Clock, ChevronRight, Award, Users, Library, Sparkles, HelpCircle, GraduationCap, Bell
} from "lucide-react";
import axios from "axios";
import InteractiveTutorial from "@/components/InteractiveTutorial";
import NotificationSettings from "@/components/NotificationSettings";
import NotificationPrompt from "@/components/NotificationPrompt";
import PhilosophicalGreeting from "@/components/PhilosophicalGreeting";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StudentDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [showPhilosophicalGreeting, setShowPhilosophicalGreeting] = useState(false);

  useEffect(() => {
    fetchDashboard();
    // Show tutorial on first visit
    const tutorialCompleted = localStorage.getItem("sophia_tutorial_completed");
    if (!tutorialCompleted) {
      setShowTutorial(true);
    } else {
      // If tutorial is done, check if we should show notification prompt
      const notificationPromptDismissed = localStorage.getItem("sophia_notification_prompt_dismissed");
      if (!notificationPromptDismissed) {
        // Small delay to let the page load
        setTimeout(() => setShowNotificationPrompt(true), 1000);
      } else {
        // Show philosophical greeting for returning users
        const lastGreeting = sessionStorage.getItem("sophia_greeting_shown");
        if (!lastGreeting) {
          setTimeout(() => setShowPhilosophicalGreeting(true), 500);
          sessionStorage.setItem("sophia_greeting_shown", "true");
        }
      }
    }
  }, [user.id]);

  const handleTutorialComplete = () => {
    setShowTutorial(false);
    // Show notification prompt after tutorial
    const notificationPromptDismissed = localStorage.getItem("sophia_notification_prompt_dismissed");
    if (!notificationPromptDismissed) {
      setTimeout(() => setShowNotificationPrompt(true), 500);
    }
  };

  const handleNotificationPromptDismiss = () => {
    setShowNotificationPrompt(false);
    // Show philosophical greeting after notification prompt
    setTimeout(() => {
      setShowPhilosophicalGreeting(true);
      sessionStorage.setItem("sophia_greeting_shown", "true");
    }, 300);
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/student/${user.id}`);
      setDashboardData(response.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateProgress = () => {
    if (!dashboardData) return 0;
    const totalNotions = 17;
    const completedNotions = Object.keys(dashboardData.notion_progress || {}).length;
    return Math.round((completedNotions / totalNotions) * 100);
  };

  const getLevel = () => user.level || 1;
  const getPoints = () => user.points || 0;
  const getNextLevelPoints = () => getLevel() * 500;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <BookOpen className="w-12 h-12 text-[#1A365D] animate-pulse mx-auto" />
          <p className="mt-4 text-[#64748B]">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="student-dashboard">
      {/* Interactive Tutorial */}
      {showTutorial && (
        <InteractiveTutorial 
          onComplete={handleTutorialComplete}
          onSkip={handleTutorialComplete}
        />
      )}

      {/* Notification Permission Prompt */}
      {showNotificationPrompt && !showTutorial && (
        <NotificationPrompt
          studentId={user.id}
          studentName={`${user.first_name} ${user.last_name}`}
          onDismiss={handleNotificationPromptDismiss}
        />
      )}

      {/* Philosophical Greeting */}
      {showPhilosophicalGreeting && !showTutorial && !showNotificationPrompt && (
        <PhilosophicalGreeting
          studentName={user.first_name}
          onDismiss={() => setShowPhilosophicalGreeting(false)}
        />
      )}
      
      {/* Navigation */}
      <nav className="navbar px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#1A365D] flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <span className="navbar-logo hidden sm:block">SophIA</span>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center gap-2 bg-[#D4AF37]/10 px-3 py-1.5 rounded-lg">
            <Star className="w-4 h-4 text-[#D4AF37]" />
            <span className="font-semibold text-[#1A365D]">{getPoints()} pts</span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowTutorial(true)}
            className="text-[#64748B] hover:text-[#1A365D]"
            data-testid="help-button"
            title="Tutoriel"
          >
            <HelpCircle className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowNotificationSettings(!showNotificationSettings)}
            className="text-[#D4AF37] hover:text-[#1A365D]"
            data-testid="notifications-button"
            title="Notifications"
          >
            <Bell className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate("/aide")}
            className="text-[#64748B] hover:text-[#1A365D]"
            data-testid="manual-button"
            title="Mode d'emploi"
          >
            <BookOpen className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onLogout}
            className="text-[#64748B] hover:text-[#1A365D]"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </nav>

      <main className="px-4 md:px-8 py-6 max-w-7xl mx-auto page-enter">
        {/* Notification Settings Panel */}
        {showNotificationSettings && (
          <div className="mb-6">
            <NotificationSettings 
              studentId={user.id} 
              studentName={`${user.first_name} ${user.last_name}`}
            />
          </div>
        )}

        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl text-[#1A365D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Bonjour, {user.first_name} !
          </h1>
          <p className="text-[#64748B]">{user.class_name} • Saint-Jean Hulst</p>
        </div>

        {/* Diagnostic Alert */}
        {!user.diagnostic_completed && (
          <Card className="mb-6 border-[#D4AF37] bg-[#D4AF37]/5" data-testid="diagnostic-alert">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Target className="w-6 h-6 text-[#D4AF37]" />
                <div>
                  <p className="font-semibold text-[#1A365D]">Évaluation diagnostique</p>
                  <p className="text-sm text-[#64748B]">Commencez par évaluer votre niveau actuel</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate("/diagnostic")}
                className="bg-[#D4AF37] hover:bg-[#B3932E] text-[#0F172A]"
                data-testid="start-diagnostic-button"
              >
                Commencer
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="stat-card">
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-sm text-[#64748B]">Niveau</span>
            </div>
            <p className="stat-value">{getLevel()}</p>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-2 mb-2">
              <Star className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-sm text-[#64748B]">Points</span>
            </div>
            <p className="stat-value">{getPoints()}</p>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-5 h-5 text-[#1A365D]" />
              <span className="text-sm text-[#64748B]">Exercices</span>
            </div>
            <p className="stat-value">{dashboardData?.total_exercises || 0}</p>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-5 h-5 text-[#22C55E]" />
              <span className="text-sm text-[#64748B]">Progression</span>
            </div>
            <p className="stat-value">{calculateProgress()}%</p>
          </div>
        </div>

        {/* Level Progress */}
        <Card className="mb-8 card-sjh">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-[#1A365D]">Progression vers le niveau {getLevel() + 1}</span>
              <span className="text-sm text-[#64748B]">{getPoints()} / {getNextLevelPoints()} pts</span>
            </div>
            <div className="progress-container">
              <div 
                className="progress-fill" 
                style={{ width: `${(getPoints() / getNextLevelPoints()) * 100}%` }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Modules */}
        <h2 className="text-xl text-[#1A365D] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          Modules d'entraînement
        </h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {/* Dissertation */}
          <div 
            className="module-card card-hover cursor-pointer"
            onClick={() => navigate("/dissertation")}
            data-testid="dissertation-module"
          >
            <div className="flex items-center justify-between mb-4">
              <PenTool className="w-8 h-8" />
              <ChevronRight className="w-5 h-5" />
            </div>
            <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Dissertation
            </h3>
            <p className="text-sm opacity-90">
              Entraînez-vous à la dissertation philosophique sur les 17 notions du programme
            </p>
          </div>

          {/* Commentaire */}
          <div 
            className="module-card card-hover cursor-pointer"
            onClick={() => navigate("/commentaire")}
            data-testid="commentaire-module"
          >
            <div className="flex items-center justify-between mb-4">
              <FileText className="w-8 h-8" />
              <ChevronRight className="w-5 h-5" />
            </div>
            <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Commentaire de texte
            </h3>
            <p className="text-sm opacity-90">
              Analysez les grands textes philosophiques du programme
            </p>
          </div>

          {/* Chat with Teacher */}
          <div 
            className="module-card module-card-gold card-hover cursor-pointer"
            onClick={() => navigate("/chat")}
            data-testid="chat-module"
          >
            <div className="flex items-center justify-between mb-4">
              <MessageCircle className="w-8 h-8" />
              <ChevronRight className="w-5 h-5" />
            </div>
            <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Question privée
            </h3>
            <p className="text-sm opacity-90">
              Discutez en privé avec les philosophes 24h/24
            </p>
          </div>

          {/* Group Chat - Agora */}
          <div 
            className="module-card card-hover cursor-pointer bg-gradient-to-br from-[#1A365D] via-[#2D4A6F] to-[#1A365D]"
            onClick={() => navigate("/chat/groupe")}
            data-testid="group-chat-module"
          >
            <div className="flex items-center justify-between mb-4">
              <Users className="w-8 h-8" />
              <ChevronRight className="w-5 h-5" />
            </div>
            <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Agora Philosophique
            </h3>
            <p className="text-sm opacity-90">
              Débattez avec votre classe et les philosophes !
            </p>
          </div>

          {/* Debate - Discuter d'un sujet */}
          <div 
            className="module-card card-hover cursor-pointer bg-gradient-to-br from-[#22C55E] via-[#16A34A] to-[#15803D]"
            onClick={() => navigate("/debate")}
            data-testid="debate-module"
          >
            <div className="flex items-center justify-between mb-4">
              <MessageCircle className="w-8 h-8" />
              <Badge className="bg-white/20 text-white text-xs">Nouveau</Badge>
            </div>
            <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Discuter d'un sujet
            </h3>
            <p className="text-sm opacity-90">
              Proposez un thème et 3 philosophes débattent !
            </p>
          </div>

          {/* Bibliothèque */}
          <div 
            className="module-card card-hover cursor-pointer bg-gradient-to-br from-[#8B5CF6] via-[#7C3AED] to-[#6D28D9]"
            onClick={() => navigate("/library")}
            data-testid="library-module"
          >
            <div className="flex items-center justify-between mb-4">
              <Library className="w-8 h-8" />
              <ChevronRight className="w-5 h-5" />
            </div>
            <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Bibliothèque
            </h3>
            <p className="text-sm opacity-90">
              Découvrez les 21 philosophes et discutez avec eux
            </p>
          </div>

          {/* Thématiques Bac - NEW */}
          <div 
            className="module-card card-hover cursor-pointer bg-gradient-to-br from-[#EC4899] via-[#DB2777] to-[#BE185D]"
            onClick={() => navigate("/thematiques")}
            data-testid="thematiques-module"
          >
            <div className="flex items-center justify-between mb-4">
              <GraduationCap className="w-8 h-8" />
              <Badge className="bg-white/20 text-white text-xs">Nouveau</Badge>
            </div>
            <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Thématiques Bac
            </h3>
            <p className="text-sm opacity-90">
              Les 17 notions et ce qu'en pensent les philosophes
            </p>
          </div>

          {/* Quiz de Remédiation */}
          <div 
            className="module-card card-hover cursor-pointer bg-gradient-to-br from-[#F59E0B] via-[#D97706] to-[#B45309]"
            onClick={() => navigate("/quiz")}
            data-testid="quiz-module"
          >
            <div className="flex items-center justify-between mb-4">
              <HelpCircle className="w-8 h-8" />
              <Badge className="bg-white/20 text-white text-xs">Bases</Badge>
            </div>
            <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Quiz Remédiation
            </h3>
            <p className="text-sm opacity-90">
              Révisez les bases avec des quiz fun !
            </p>
          </div>
        </div>

        {/* Badges */}
        {user.badges && user.badges.length > 0 && (
          <Card className="mb-8 card-sjh">
            <CardHeader>
              <CardTitle className="text-lg text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Vos badges
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="badge-container">
                {user.badges.map((badge, index) => (
                  <div key={index} className="badge-item badge-shine">
                    <img 
                      src="https://static.prod-images.emergentagent.com/jobs/98d25985-47f7-4564-8730-c3235c423117/images/58ed156620483fe85399e3efb21014dbf198368ad1152ff587c82a27bb1d68b9.png"
                      alt={badge}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Activity */}
        {dashboardData?.recent_attempts?.length > 0 && (
          <Card className="card-sjh">
            <CardHeader>
              <CardTitle className="text-lg text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Activité récente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboardData.recent_attempts.slice(0, 3).map((attempt, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-[#F8F9FA] rounded-lg">
                    <div className="flex items-center gap-3">
                      <Clock className="w-4 h-4 text-[#64748B]" />
                      <div>
                        <p className="text-sm font-medium text-[#1A365D]">Exercice terminé</p>
                        <p className="text-xs text-[#64748B]">
                          {new Date(attempt.submitted_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                    <Badge className="bg-[#22C55E] text-white">
                      {attempt.score}/20
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Leaderboard Link */}
        <div className="mt-6 text-center">
          <Button 
            variant="outline" 
            onClick={() => navigate("/leaderboard")}
            className="border-[#1A365D] text-[#1A365D] hover:bg-[#1A365D] hover:text-white"
            data-testid="leaderboard-button"
          >
            <Trophy className="w-4 h-4 mr-2" />
            Voir le classement
          </Button>
        </div>
      </main>
    </div>
  );
};

export default StudentDashboard;
